import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ClientLayout from '../../components/ClientLayout';
import PageHeader from '../../components/client/PageHeader';
import BookingCard from '../../components/client/bookings/BookingCard';
import BookingsEmptyState from '../../components/client/bookings/BookingsEmptyState';
import BookingTabs from '../../components/client/bookings/BookingTabs';
import { clientPortalApi } from '../../lib/http';

const MyBookings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');

  const {
    data: bookings,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['client-bookings', activeTab],
    queryFn: async () => {
      const response = await clientPortalApi.bookings.list(activeTab);
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
  });

  return (
    <ClientLayout>
      <div className="w-full">
        {/* Page Header */}
        <PageHeader title="My Bookings" backTo="/u" />

        {/* Tabs */}
        <div className="mb-2 sm:mb-3">
          <BookingTabs activeTab={activeTab} onTabChange={setActiveTab} />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 animate-pulse">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading bookings</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{(error as any)?.message || 'Failed to load your bookings. Please try again.'}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => refetch()}
                    className="px-3 py-2 bg-red-100 text-red-800 rounded-md text-sm font-medium hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success State - Bookings List */}
        {!isLoading && !isError && bookings && (
          <>
            {bookings.length === 0 ? (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-12 text-center">
                <svg
                  className="mx-auto h-16 w-16 text-gray-300 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {activeTab === 'upcoming' ? 'No Upcoming Bookings' : 'No Past Bookings'}
                </h3>
                <p className="text-sm text-gray-600">
                  {activeTab === 'upcoming'
                    ? 'You don\'t have any scheduled appointments yet.'
                    : 'You don\'t have any booking history yet.'}
                </p>
              </div>
            ) : (
              <>
                {/* Bookings Count */}
                <div className="mb-3 sm:mb-4">
                  <p className="text-xs sm:text-sm text-gray-600">
                    {bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'}
                    {activeTab === 'upcoming' ? ' upcoming' : ' in history'}
                  </p>
                </div>

                {/* Bookings List */}
                <div className="space-y-3 sm:space-y-4">
                  {bookings.map((booking) => (
                    <BookingCard key={booking.id} booking={booking} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </ClientLayout>
  );
};

export default MyBookings;
